// Wire types of the video-project module (kept in step with
// fastify/modules/video-project/http/video-project.schema.ts).

export type VideoMode = 'presenter' | 'explainer';
export type VideoProjectStatus = 'draft' | 'editing' | 'preview_ready' | 'published';
export type RenderJobStatus =
  | 'queued'
  | 'preparing'
  | 'generating_audio'
  | 'aligning_audio'
  | 'rendering'
  | 'encoding'
  | 'uploading'
  | 'complete'
  | 'failed';

export interface BrandKit {
  background: string;
  text: string;
  outline: string;
  fill: string;
}

export const DEFAULT_BRAND_KIT: BrandKit = {
  background: '#FFFFFF',
  text: '#1C1C1C',
  outline: '#1C1C1C',
  fill: '#F79420'
};
export const BRAND_KIT_ROLES: Array<{ key: keyof BrandKit; label: string; hint: string }> = [
  { key: 'background', label: 'Background', hint: 'Behind every scene' },
  { key: 'text', label: 'Text', hint: 'Titles, labels, captions' },
  { key: 'outline', label: 'Outline', hint: 'Line-art strokes' },
  { key: 'fill', label: 'Fill', hint: 'The one accent that carries emphasis' }
];
export const HEX_COLOR_RE = /^#[0-9A-Fa-f]{6}$/;

export interface NarrationAudio {
  hash: string;
  url: string;
  durationSeconds: number;
  alignmentUrl: string | null;
}

export interface PresenterSection {
  id: string;
  position: number;
  title: string;
  narration: string;
  estimatedSeconds: number;
  audio: NarrationAudio | null;
}

export type SceneType =
  | 'statement'
  | 'key_points'
  | 'two_column'
  | 'numbered_steps'
  | 'scenario_card'
  | 'stat_highlight'
  | 'diagram'
  | 'summary';

export type DiagramLayout = 'spoke_and_wheel' | 'hierarchy' | 'flow' | 'grid_2x2';

export interface ExplainerScene {
  id: string;
  position: number;
  type: SceneType;
  content: Record<string, any>;
  narration: string;
  reasoning: string | null;
  estimatedSeconds: number;
  audio: NarrationAudio | null;
}

export interface Budget {
  totalSeconds: number;
  budgetSeconds: number;
  remainingSeconds: number;
  withinBudget: boolean;
  lockedSeconds: number;
}

export interface VideoProject {
  id: string;
  programId: string;
  moduleId: string;
  lessonId: string;
  mode: VideoMode;
  status: VideoProjectStatus;
  targetDurationSeconds: number;
  guidance: string;
  contentVersion: number;
  previewVersion: number | null;
  previewStale: boolean;
  previewUrl: string | null;
  previewThumbnailUrl: string | null;
  previewDurationSeconds: number | null;
  publishedVersion: number | null;
  activeRenderJobId: string | null;
  brandKitSnapshot: BrandKit | null;
  sections: PresenterSection[];
  scenes: ExplainerScene[];
  budget: Budget;
  lastError: { code: string; message: string } | null;
  createdAt: string;
  updatedAt: string;
}

export interface RenderJob {
  renderJobId: string;
  projectId: string;
  projectVersion: number;
  type: 'preview' | 'final';
  status: RenderJobStatus;
  progress: number;
  progressMessage: string;
  outputUrl: string | null;
  thumbnailUrl: string | null;
  durationSeconds: number | null;
  promoted: boolean;
  errorCode: string | null;
  errorMessage: string | null;
  createdAt: string;
  completedAt: string | null;
}

export interface LessonVideoAttachment {
  id: string;
  projectId: string;
  projectVersion: number;
  mode: VideoMode;
  videoUrl: string;
  thumbnailUrl: string | null;
  durationSeconds: number;
  createdAt: string;
  updatedAt: string;
}

export interface Allowance {
  used: number;
  limit: number;
  remaining: number;
}

export interface LessonVideoStatus {
  attachment: LessonVideoAttachment | null;
  stale: boolean;
  project: VideoProject | null;
  activeJob: RenderJob | null;
  allowance: Allowance;
  brandKit: BrandKit;
}

export interface LearnerLessonVideo {
  videoUrl: string;
  thumbnailUrl: string | null;
  durationSeconds: number;
  mode: VideoMode;
}

// Prototype "LENGTH" options; the API accepts 30 too (central config) but
// the product exposes these three.
export const DURATION_OPTIONS: Array<{ seconds: number; label: string; hint: string }> = [
  { seconds: 60, label: '60 sec', hint: 'Tight & punchy' },
  { seconds: 90, label: '90 sec', hint: 'Recommended' },
  { seconds: 120, label: '2 min', hint: 'In-depth' }
];

export const MODE_LABEL: Record<VideoMode, string> = { presenter: 'Presenter', explainer: 'Explainer' };
export const MODE_NOUN: Record<VideoMode, string> = { presenter: 'script', explainer: 'storyboard' };

export const isTerminalJob = (s: RenderJobStatus | undefined | null): boolean => s === 'complete' || s === 'failed';
